import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { listMovements, type WalletMovementFull } from "@/lib/api";
import { formatAmount, formatDateTime } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, type ColumnDef } from "@/components/DataTable";
import { Download } from "lucide-react";
import {
  aggregateBankTransfersByDay,
  isBankTransfer,
  BANK_TRANSFER_DESCRIPTION,
} from "@/lib/movements";

const TYPES = [
  { value: "credit_payment", label: "Crédit paiement" },
  { value: "debit_refund", label: "Débit refund" },
  { value: "debit_settlement", label: "Débit settlement" },
  { value: "credit_settlement_failed", label: "Annulation settlement" },
  { value: "debit_commission", label: "Commission" },
  { value: "manual_adjustment", label: "Bank Transfer" },
];

export function HistoryPage() {
  const [movements, setMovements] = useState<WalletMovementFull[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [type, setType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = async (reset = false) => {
    setLoading(true);
    try {
      const params: any = { limit: 100 };
      if (!reset && cursor) params.cursor = cursor;
      if (type) params.movement_type = type;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const r = await listMovements(params);
      setMovements(reset ? r.data : [...movements, ...r.data]);
      setCursor(r.next_cursor ?? null);
      setHasMore(r.has_more);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCursor(null);
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, from, to]);

  // Historique = vue cross-wallets → agrégation Bank Transfers par jour, tous
  // wallets confondus (miroir de /finance/statements en mode "Tous les wallets").
  const displayedMovements = useMemo(
    () => aggregateBankTransfersByDay(movements, { acrossWallets: true }),
    [movements],
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      // Fetch toutes les pages avec les filtres actifs
      const all: WalletMovementFull[] = [];
      let cur: string | undefined;
      for (let i = 0; i < 100; i++) {
        const params: any = { limit: 200 };
        if (cur) params.cursor = cur;
        if (type) params.movement_type = type;
        if (from) params.date_from = from;
        if (to) params.date_to = to;
        const r = await listMovements(params);
        all.push(...r.data);
        if (!r.has_more || !r.next_cursor) break;
        cur = r.next_cursor;
      }
      // Miroir Settlements/Statements : Bank Transfers agrégés par jour tous
      // wallets confondus. Sinon les refs synthétiques polluent l'export.
      const aggregated = aggregateBankTransfersByDay(all, { acrossWallets: true });
      const rows = aggregated.map((m) => {
        const bt = isBankTransfer(m);
        return {
          Date: m.created_at,
          Wallet: bt ? "Tous wallets" : m.wallet_type === "mobile_money" ? "Mobile Money" : "Cartes",
          "Devise wallet": m.wallet_currency,
          Type: bt ? "Bank Transfer" : m.display_type ?? labelType(m.type),
          // "Type technique" / "Reference type" laissent leur valeur brute
          // sauf "manual" (technique DB, aucun sens métier) → chaîne vide.
          "Type technique": m.type === "manual_adjustment" ? "" : m.type,
          "Reference type": m.reference_type === "manual" ? "" : m.reference_type,
          "Reference ID": m.reference_id,
          Devise: m.currency,
          Montant: m.amount_minor / 100,
          "Solde apres": m.balance_after_minor / 100,
          Description: bt ? BANK_TRANSFER_DESCRIPTION : m.description ?? "",
        };
      });
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, "Historique");
      const today = new Date().toISOString().slice(0, 10);
      const suffix = [from, to].filter(Boolean).join("_a_") || today;
      XLSX.writeFile(wb, `historique-${suffix}.xlsx`);
    } catch (e) {
      console.error("export failed", e);
      alert("Erreur lors de l'export.");
    } finally {
      setExporting(false);
    }
  };

  const columns: ColumnDef<WalletMovementFull>[] = [
    {
      key: "date",
      header: "Date",
      minWidth: 130,
      sortable: true,
      accessor: (m) => m.created_at ?? "",
      cell: (m) => <span className="text-xs text-[#3f4254] font-mono">{formatDateTime(m.created_at)}</span>,
    },
    {
      key: "wallet",
      header: "Wallet",
      minWidth: 160,
      cell: (m) => (
        <div>
          <div className="text-sm font-semibold">{m.wallet_type === "mobile_money" ? "Mobile Money" : "Cartes"}</div>
          <div className="text-xs text-[#7e8299]">{m.wallet_currency}</div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      minWidth: 170,
      cell: (m) =>
        isBankTransfer(m) ? (
          <Badge color="info">Bank Transfer</Badge>
        ) : (
          <Badge color={movementColor(m.type)}>{m.display_type ?? labelType(m.type)}</Badge>
        ),
    },
    {
      key: "ref",
      header: "Référence",
      minWidth: 220,
      wrap: true,
      cell: (m) => (
        <div>
          {/* Ne pas afficher le mot "Manual" (reference_type technique DB) —
              cette catégorie n'a pas de sens métier côté marchand. */}
          {m.reference_type !== "manual" && (
            <div className="text-xs text-[#7e8299] capitalize">{m.reference_type}</div>
          )}
          <div className="font-mono text-xs text-[#181c32]">{m.reference_id}</div>
        </div>
      ),
    },
    {
      key: "amount",
      header: "Montant",
      align: "right",
      minWidth: 130,
      cell: (m) => (
        <span className={`font-bold ${m.amount_minor >= 0 ? "text-success-700" : "text-danger-700"}`}>
          {m.amount_minor >= 0 ? "+" : ""}
          {formatAmount(m.amount_minor, m.currency)}
        </span>
      ),
    },
    {
      key: "balance",
      header: "Solde après",
      align: "right",
      minWidth: 130,
      cell: (m) => <span className="text-sm text-[#181c32]">{formatAmount(m.balance_after_minor, m.currency)}</span>,
    },
    {
      key: "desc",
      header: "Description",
      minWidth: 240,
      wrap: true,
      cell: (m) => (
        <span className="text-xs text-[#3f4254]">
          {isBankTransfer(m) ? BANK_TRANSFER_DESCRIPTION : m.description || "—"}
        </span>
      ),
    },
  ];

  const filters = (
    <>
      <select value={type} onChange={(e) => setType(e.target.value)} className="form-input py-2 px-3 text-sm w-auto">
        <option value="">Tous types</option>
        {TYPES.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>
      <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="form-input py-2 px-3 text-sm w-auto" placeholder="Du" />
      <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="form-input py-2 px-3 text-sm w-auto" placeholder="Au" />
      {(from || to || type) && (
        <button onClick={() => { setType(""); setFrom(""); setTo(""); }} className="btn btn-light btn-sm">
          Réinitialiser
        </button>
      )}
      <button
        onClick={handleExport}
        disabled={exporting || movements.length === 0}
        className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 hover:bg-brand-800 disabled:bg-brand-300 disabled:cursor-not-allowed text-white px-3 py-2 text-sm font-semibold transition-colors"
        title="Exporter en Excel (respecte les filtres actifs)"
      >
        <Download size={14} />
        {exporting ? "Export…" : "Exporter Excel"}
      </button>
    </>
  );

  return (
    <div>
      <PageHeader title="Historique" breadcrumbs={[{ label: "Finance" }, { label: "Historique" }]} />

      <DataTable<WalletMovementFull>
        title="Toutes les opérations"
        subtitle="Tous les mouvements de tous vos wallets"
        columns={columns}
        data={displayedMovements}
        rowKey={(m) => m.id}
        loading={loading && movements.length === 0}
        filtersSlot={filters}
        loadMore={hasMore ? { hasMore, onLoadMore: () => load(false), loading } : undefined}
        initialSort={{ key: "date", dir: "desc" }}
      />
    </div>
  );
}

function labelType(t: string): string {
  return (
    {
      credit_payment: "Crédit paiement",
      debit_refund: "Débit remboursement",
      debit_settlement: "Débit settlement",
      credit_settlement_failed: "Annulation settlement",
      debit_commission: "Commission",
      manual_adjustment: "Bank Transfer",
    } as Record<string, string>
  )[t] ?? t;
}

function movementColor(t: string): "success" | "danger" | "warning" | "info" | "light" {
  if (t.startsWith("credit_")) return "success";
  if (t === "debit_refund") return "danger";
  if (t === "debit_commission") return "warning";
  if (t === "debit_settlement") return "info";
  return "light";
}
